import { useRef, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router";
import CountryPhoneInput from "@/customComponents/authComponents/CountryPhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { consumeRedirectAfterLogin } from "@/utils/functions";
import SocialLoginButtons from "@/customComponents/authComponents/SocialLoginButtons";
import AuthLayout from "./AuthLayout";
import { otpFromResponse } from "@/utils/otpFromResponse";
import { OTP_CHANNEL_UNAVAILABLE } from "@/utils/otpChannel";

function getActiveIdentifier() {
  try {
    return JSON.parse(sessionStorage.getItem('hopn_otp_active')) || null;
  } catch { return null; }
}

function getSession(identifier) {
  try {
    const all = JSON.parse(sessionStorage.getItem('hopn_otp_sessions')) || {};
    return all[identifier] || null;
  } catch { return null; }
}

function hasActiveTimer(identifier) {
  const session = getSession(identifier);
  if (!session?.otpSentAt) return false;
  const elapsed = Math.floor((Date.now() - session.otpSentAt) / 1000);
  return elapsed < 300;
}

export default function SignIn() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const wasAuthenticatedOnMount = useRef(isAuthenticated);
  const active = getActiveIdentifier();
  const [activeTab, setActiveTab] = useState(active?.otpMethod || "email");
  const [email, setEmail] = useState(active?.otpMethod === "email" ? active?.identifier || "" : "");
  const [phoneNumber, setPhoneNumber] = useState(active?.otpMethod === "phone" ? active?.identifier || "" : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect only if user was already logged in when landing here
  // (avoid racing with imperative navigate() in social/OTP login flows)
  if (wasAuthenticatedOnMount.current) {
    return <Navigate to={consumeRedirectAfterLogin() || "/"} replace />;
  }

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (activeTab === "email") {
      if (!email.trim()) {
        setError("Email is required");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("Email is not valid");
        return;
      }

      // If timer is still running for this email, go straight to OTP page
      if (hasActiveTimer(email)) {
        navigate("/otp-verification");
        return;
      }

      setLoading(true);
      const result = await authService.loginWithEmail(email, "passenger");
      setLoading(false);

      if (result.success) {
        // `prefill` rides along only when the backend returned the code it just sent,
        // which it does for TEST accounts only — see `utils/otpFromResponse`.
        navigate("/otp-verification", {
          // `channel` is the SERVER'S word for where the code went, carried alongside the
          // method so the next screen states it rather than inferring it from this tab.
          state: { otpMethod: "email", channel: result.channel, identifier: email, prefill: otpFromResponse(result.data) },
        });
      } else if (result.key === OTP_CHANNEL_UNAVAILABLE) {
        // Nothing to send TO on this channel — a placeholder address, typically from a
        // phone-desk booking. Retrying the same field can never work now that a code goes
        // to one channel only, so the other identifier is the whole answer.
        setError("We don't have a usable email for this account. Please sign in with your phone number instead.");
      } else {
        setError(result.message);
      }
    } else {
      if (!phoneNumber) {
        setError("Phone number is required");
        return;
      }
      if (!isValidPhoneNumber(phoneNumber)) {
        setError("Phone number is not valid");
        return;
      }

      // If timer is still running for this phone, go straight to OTP page
      if (hasActiveTimer(phoneNumber)) {
        navigate("/otp-verification");
        return;
      }

      setLoading(true);
      const result = await authService.loginWithPhone(phoneNumber, "passenger");
      setLoading(false);

      if (result.success) {
        navigate("/otp-verification", {
          state: { otpMethod: "phone", channel: result.channel, identifier: phoneNumber, prefill: otpFromResponse(result.data) },
        });
      } else if (result.key === OTP_CHANNEL_UNAVAILABLE) {
        setError("We don't have a phone number on this account. Please sign in with your email instead.");
      } else {
        setError(result.message);
      }
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div>
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-2xl sm:text-3xl text-center">
                Login
              </h1>
            </div>
            <div>
              {/* Social login FIRST — directly under the heading and above the tabs.
                  Matches the booking portal: Google and Apple are the fastest way in, so
                  they lead the screen. The tabs only pick which flavour of the manual
                  form follows, so they belong after the "or". */}
              <SocialLoginButtons disabled={loading} large />

              {/* or */}
              <div className="relative py-3 sm:py-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 py-1 text-gray-800 bg-emerald-200 rounded-full">
                    or
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <button
                  onClick={() => switchTab("email")}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === "email"
                      ? "bg-emerald-200 text-gray-800"
                      : "bg-white text-gray-600 border border-gray-300"
                  }`}
                >
                  Login with Email
                </button>
                <button
                  onClick={() => switchTab("phone")}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    activeTab === "phone"
                      ? "bg-emerald-200 text-gray-800"
                      : "bg-white text-gray-600 border border-gray-300"
                  }`}
                >
                  Login with Phone
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {activeTab === "email" ? (
                    <div>
                      <input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError("");
                        }}
                        className="h-14 w-full rounded-full border border-gray-300 bg-transparent px-6 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div className="auth-phone-input">
                      <CountryPhoneInput
                        defaultCountry="ca"
                        value={phoneNumber}
                        onChange={(phone) => {
                          setPhoneNumber(phone);
                          setError("");
                        }}
                      />
                    </div>
                  )}

                  {error && (
                    <p className="text-sm text-red-500 text-center">{error}</p>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative flex items-center justify-center w-full h-14 rounded-full bg-gray-900 text-white text-sm font-medium transition hover:bg-gray-800 disabled:opacity-60"
                    >
                      {loading ? "Sending OTP..." : "Continue"}
                      <span className="absolute right-2 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-300">
                        <svg className="size-5 text-gray-900" viewBox="0 0 20 20" fill="none">
                          <path d="M4.167 10h11.666M10.833 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </form>

              {/* Or divider */}
              <div className="relative py-3 sm:py-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 py-1 text-gray-800 bg-emerald-200 rounded-full">
                    or
                  </span>
                </div>
              </div>

              {/* Register link — only "Register" is underlined + clickable */}
              <div className="text-center">
                <p className="text-[15px] font-normal text-gray-700">
                  New user?{" "}
                  <Link
                    to="/signup"
                    className="font-medium text-gray-800 hover:text-gray-600 underline"
                  >
                    Register
                  </Link>
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
